import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { GlanceCard } from '../shell/GlanceCard';
import { AlertRow } from '../shell/AlertRow';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { RelationsTable } from './RelationsTable';
import { WarHistoryTable } from './WarHistoryTable';
import { WarBenefitsPanel } from './WarBenefitsPanel';
import { tabStyle, btnStyle, AT_WAR_COLOR, CEASEFIRE_COLOR } from './relationsPresentation';
import type { RelationsShellProps, RelationsTab } from '../../pages/Relations';

const TABS: { key: RelationsTab; label: (activeWar: boolean) => string }[] = [
  { key: 'overview', label: () => 'Overview' },
  { key: 'war', label: (activeWar) => `War${activeWar ? ' ⚔' : ''}` },
  { key: 'history', label: () => 'History' },
];

const mobileInputStyle: React.CSSProperties = {
  padding: '8px',
  fontSize: 13,
  minHeight: 44,
  backgroundColor: COLORS.cardAlt,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 4,
  color: COLORS.text.primary,
  width: '100%',
  boxSizing: 'border-box',
};

/**
 * SF-158: mobile card-based content for Relations — 3-tab structure (Overview/War/History)
 * restacked into GlanceCard sections. No sticky bottom bar — each tab's action is a single
 * independent decision point, not an accumulating multi-row order (matches SF-139/149/153).
 */
export function MobileRelationsShell({
  data,
  isLoading,
  error,
  activeTab,
  setActiveTab,
  busyAction,
  isMonarch,
  activeWar,
  declareKingdomNumber,
  setDeclareKingdomNumber,
  declareIslandNumber,
  setDeclareIslandNumber,
  declareBlockedReason,
  warStartedText,
  mutationVersion,
  handleDeclareWarRequest,
  handleProposePeace,
  handleWithdrawRequest,
  handleCancelCeasefireRequest,
}: RelationsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  useEffect(() => {
    if (mutationVersion > 0) {
      h1Ref.current?.focus();
    }
  }, [mutationVersion]);

  const declareWarBusy = busyAction === 'declareWar';
  const proposePeaceBusy = busyAction === 'proposePeace';
  const withdrawBusy = busyAction === 'withdraw';
  const cancelCeasefireBusy = busyAction === 'cancelCeasefire';

  const declareDisabled = declareWarBusy || !declareKingdomNumber.trim() || !declareIslandNumber.trim();

  return (
    <div className="space-y-3">
      <h1
        ref={h1Ref}
        tabIndex={-1}
        className="outline-none"
        style={{ margin: 0, color: COLORS.text.primary, fontSize: 18, fontWeight: 700 }}
      >
        Relations
      </h1>

      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Relations sections"
        style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, gap: 0 }}
      >
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            id={`relations-tab-${key}`}
            role="tab"
            aria-selected={activeTab === key}
            aria-controls={`relations-panel-${key}`}
            onClick={() => {
              setActiveTab(key);
              // SF-173-style: in-page tab switch, not a route change — Relations.tsx runs under
              // legacy document-scrolling Layout.tsx on both platforms (CLAUDE.md).
              window.scrollTo(0, 0);
            }}
            style={{ ...tabStyle(activeTab === key), flex: 1, minHeight: 44, textAlign: 'center' }}
          >
            {label(Boolean(activeWar))}
          </button>
        ))}
      </div>

      {isLoading && !data && <Spinner />}
      {error && !data && <ErrorMessage message={error} />}

      {/* ─── Overview Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div role="tabpanel" id="relations-panel-overview" aria-labelledby="relations-tab-overview" className="space-y-3">
          {data && (
            <>
              <RelationsTable relationships={data.relationships} activeWar={activeWar} layout="stacked" />

              {!activeWar && isMonarch && (
                <GlanceCard title="Declare War">
                  <div style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                    Target kingdom must be Hostile toward you.
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1" style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                      Kingdom
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={declareKingdomNumber}
                        onChange={(e) => setDeclareKingdomNumber(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={mobileInputStyle}
                      />
                    </label>
                    <label className="flex-1" style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                      Island
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={declareIslandNumber}
                        onChange={(e) => setDeclareIslandNumber(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={mobileInputStyle}
                      />
                    </label>
                  </div>
                  {declareBlockedReason && (
                    <div style={{ fontSize: 12, color: COLORS.warning }}>{declareBlockedReason}</div>
                  )}
                  <button
                    onClick={handleDeclareWarRequest}
                    disabled={declareDisabled}
                    aria-busy={declareWarBusy}
                    style={{ ...btnStyle('danger', declareDisabled), minHeight: 44, width: '100%' }}
                  >
                    Declare War
                  </button>
                </GlanceCard>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── War Tab ──────────────────────────────────────────────────────────── */}
      {activeTab === 'war' && (
        <div role="tabpanel" id="relations-panel-war" aria-labelledby="relations-tab-war" className="space-y-3">
          {activeWar ? (
            <>
              <GlanceCard title="At War">
                <div style={{ fontSize: 15, fontWeight: 600, color: AT_WAR_COLOR }}>
                  vs {activeWar.opponentKingdomName ?? activeWar.opponentKingdomId} ({activeWar.opponentKingdomId})
                </div>
                <div style={{ fontSize: 12, color: COLORS.text.secondary }}>
                  Started {warStartedText}
                  {activeWar.inCeasefire && (
                    <span style={{ marginLeft: 8, color: CEASEFIRE_COLOR, fontWeight: 600 }}>
                      · Ceasefire
                    </span>
                  )}
                </div>
              </GlanceCard>

              <GlanceCard title="War Ledger">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Land
                    </div>
                    <div style={{ fontSize: 13, color: COLORS.text.primary, marginTop: 2 }}>
                      Captured {activeWar.ledgerSummary.totalLandCaptured.toLocaleString()} · Lost {activeWar.ledgerSummary.totalLandLost.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Casualties
                    </div>
                    <div style={{ fontSize: 13, color: COLORS.text.primary, marginTop: 2 }}>
                      Ours {activeWar.ledgerSummary.totalCasualties.toLocaleString()} · Theirs {activeWar.ledgerSummary.totalOpponentCasualties.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.text.tertiary, marginTop: 4 }}>
                  † Not yet tracked — coming soon
                </div>
              </GlanceCard>

              <WarBenefitsPanel activeWar={activeWar} />

              {isMonarch && (
                <div className="flex flex-col gap-2">
                  {!activeWar.inCeasefire && (
                    <>
                      <button
                        onClick={handleProposePeace}
                        disabled={proposePeaceBusy}
                        aria-busy={proposePeaceBusy}
                        style={{ ...btnStyle('secondary', proposePeaceBusy), minHeight: 44, width: '100%' }}
                      >
                        Propose Mutual Peace
                      </button>
                      <button
                        onClick={handleWithdrawRequest}
                        disabled={withdrawBusy}
                        aria-busy={withdrawBusy}
                        style={{ ...btnStyle('secondary', withdrawBusy), minHeight: 44, width: '100%' }}
                      >
                        Withdraw
                      </button>
                    </>
                  )}
                  {activeWar.inCeasefire && (
                    <button
                      onClick={handleCancelCeasefireRequest}
                      disabled={cancelCeasefireBusy}
                      aria-busy={cancelCeasefireBusy}
                      style={{ ...btnStyle('secondary', cancelCeasefireBusy), minHeight: 44, width: '100%' }}
                    >
                      Cancel Ceasefire
                    </button>
                  )}
                </div>
              )}
              {!isMonarch && (
                <p style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                  War actions require Monarch status.
                </p>
              )}
            </>
          ) : (
            <AlertRow severity="info" message="Your kingdom is not currently at war." />
          )}
        </div>
      )}

      {/* ─── History Tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div role="tabpanel" id="relations-panel-history" aria-labelledby="relations-tab-history">
          {data && <WarHistoryTable warHistory={data.warHistory} layout="stacked" />}
        </div>
      )}
    </div>
  );
}
